// @tennis-matches/ui/tournament-setup/PlayerInput
// React hook for player input management

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  autoFillGaps,
  countValidPlayers,
  getFieldsToShow,
} from "./playerInputUtils.js";

/**
 * React hook for managing player input state
 * @param {Object} config - Configuration object
 * @param {Object} config.initialNames - Initial names object
 * @param {number} config.minFields - Minimum number of fields to show
 * @param {Function} config.onChange - Callback when names change (names, count)
 * @returns {Object} Player input state and handlers
 */
export function usePlayerInput({ initialNames = {}, minFields = 4, onChange }) {
  const [names, setNames] = useState(initialNames);
  const onChangeRef = useRef(onChange);
  const namesRef = useRef(names); // Keep ref in sync with state

  // Update ref when onChange changes (avoid stale closures)
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep namesRef in sync with names state
  useEffect(() => {
    namesRef.current = names;
  }, [names]);

  // Sync names when initialNames changes from parent
  useEffect(() => {
    if (!initialNames || Object.keys(initialNames).length === 0) return;
    setNames((prev) => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(initialNames)) return prev;
      } catch {
        /* ignore */
      }
      return initialNames;
    });
  }, [initialNames]);

  // Ensure minimum fields exist when minFields changes
  useEffect(() => {
    if (
      !names ||
      typeof names !== "object" ||
      Object.keys(names).length === 0
    ) {
      const initialNames = {};
      for (let i = 1; i <= minFields; i++) {
        initialNames[String(i)] = "";
      }
      setNames(initialNames);
      return;
    }

    const existingKeys = Object.keys(names)
      .map(Number)
      .sort((a, b) => a - b);
    const maxKey = existingKeys.length > 0 ? Math.max(...existingKeys) : 0;

    if (maxKey < minFields) {
      const newNames = { ...names };
      for (let i = maxKey + 1; i <= minFields; i++) {
        if (!newNames[String(i)]) {
          newNames[String(i)] = "";
        }
      }
      setNames(newNames);
    }
  }, [minFields]); // Only react to minFields changes

  // Calculate fields to show (for backward compatibility)
  const fieldsToShow = useMemo(() => {
    return getFieldsToShow(names, minFields);
  }, [names, minFields]);

  // Calculate player count
  const playerCount = useMemo(() => countValidPlayers(names), [names]);

  // Handle name change: update local state AND propagate to parent immediately
  // (deferred to a microtask so we never schedule a parent setState mid-render).
  // Without the parent sync, names typed shortly before navigation away (e.g.
  // clicking "Fertig") get lost because onBlur (the previous sync path) fires
  // too late.
  const handleNameChange = useCallback((idx, value) => {
    setNames((prev) => {
      const updated = { ...prev, [String(idx)]: value };
      namesRef.current = updated;
      return updated;
    });
    const notify = () => {
      if (!onChangeRef.current) return;
      const current = namesRef.current;
      onChangeRef.current(current, countValidPlayers(current));
    };
    if (typeof queueMicrotask === "function") {
      queueMicrotask(notify);
    } else {
      Promise.resolve().then(notify);
    }
  }, []);

  // Handle blur (auto-fill gaps and notify parent)
  // This is called when user manually types and blurs (not when autocomplete is selected)
  // Must NOT call parent onChange inside setState updater (causes "Cannot update component while rendering another")
  const handleNameBlur = useCallback(() => {
    setTimeout(() => {
      setNames((prev) => {
        const filled = autoFillGaps(prev, minFields);
        const count = countValidPlayers(filled);
        // Defer parent notification to next tick so it runs after React has committed this update
        setTimeout(() => {
          if (onChangeRef.current) {
            onChangeRef.current(filled, count);
          }
        }, 0);
        return filled;
      });
    }, 100);
  }, [minFields]);

  // Direct update without auto-fill (for autocomplete selection)
  // Use setTimeout to ensure onChange is called after render phase
  // Use namesRef to get the latest names value (always up-to-date)
  const handleDirectUpdate = useCallback(() => {
    // Use setTimeout to defer the onChange call to avoid render-phase updates
    setTimeout(() => {
      // Use ref to get the latest names value (always current)
      const currentNames = namesRef.current;
      const count = countValidPlayers(currentNames);
      if (onChangeRef.current) {
        onChangeRef.current(currentNames, count);
      }
    }, 0);
  }, []);

  // Add one more player field (max existing index + 1)
  const addFieldIfNeeded = useCallback(() => {
    setNames((prev) => {
      const keys = Object.keys(prev || {})
        .map(Number)
        .filter((n) => !isNaN(n) && n > 0);
      const maxKey = keys.length > 0 ? Math.max(...keys) : 0;
      const nextIndex = maxKey + 1;
      const updated = { ...prev, [String(nextIndex)]: "" };
      const count = countValidPlayers(updated);
      setTimeout(() => {
        if (onChangeRef.current) {
          onChangeRef.current(updated, count);
        }
      }, 0);
      return updated;
    });
  }, []);

  return {
    names,
    setNames,
    fieldsToShow,
    playerCount,
    handleNameChange,
    handleNameBlur,
    handleDirectUpdate,
    addFieldIfNeeded,
  };
}
